import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Avatar,
  TextField, InputAdornment, LinearProgress, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { Savings as SavingsIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';

interface Desafio {
  id: number;
  user_id: string;
  titulo: string;
  emoji: string;
  valor_objetivo: number;
  valor_poupado: number;
  estado: 'ACTIVO' | 'CONCLUIDO' | 'CANCELADO';
  pontos_ganhos: number;
  prazo: string | null;
  created_at: string;
}

const estadoColor: Record<string, 'success' | 'warning' | 'default'> = {
  ACTIVO: 'warning', CONCLUIDO: 'success', CANCELADO: 'default',
};

const fmt = (v: number) => `${v.toLocaleString('pt-CV')} ECV`;

const SavingsAdminPage: React.FC = () => {
  const [desafios, setDesafios] = useState<Desafio[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  useEffect(() => {
    api.get('/admin/savings/all')
      .then(r => setDesafios(r.data.data || []))
      .catch(() => setError('Erro ao carregar desafios'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = desafios.filter(d => {
    if (filterEstado && d.estado !== filterEstado) return false;
    if (search) {
      const q = search.toLowerCase();
      return d.titulo.toLowerCase().includes(q) || d.user_id.toLowerCase().includes(q);
    }
    return true;
  });

  const totalPoupado = desafios.filter(d => d.estado === 'ACTIVO').reduce((s, d) => s + Number(d.valor_poupado), 0);
  const concluidos = desafios.filter(d => d.estado === 'CONCLUIDO').length;
  const totalPts = desafios.reduce((s, d) => s + d.pontos_ganhos, 0);

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: '#10B981', width: 48, height: 48 }}><SavingsIcon /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Desafios de Poupança</Typography>
          <Typography variant="body2" color="text.secondary">
            Todos os desafios de poupança dos utilizadores Pagali
          </Typography>
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Total activo (ECV)', value: fmt(totalPoupado), color: '#10B981' },
          { label: 'Desafios concluídos', value: concluidos, color: '#6233A0' },
          { label: 'Pontos distribuídos', value: totalPts.toLocaleString(), color: '#F59E0B' },
        ].map(s => (
          <Paper key={s.label} sx={{ p: 2, minWidth: 160, flex: 1 }}>
            <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField size="small" placeholder="Pesquisar por título ou user_id..."
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 2, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filterEstado} label="Estado" onChange={e => setFilterEstado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="ACTIVO">Activo</MenuItem>
            <MenuItem value="CONCLUIDO">Concluído</MenuItem>
            <MenuItem value="CANCELADO">Cancelado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Desafio</b></TableCell>
                <TableCell><b>Utilizador</b></TableCell>
                <TableCell><b>Progresso</b></TableCell>
                <TableCell align="right"><b>Poupado</b></TableCell>
                <TableCell align="right"><b>Objetivo</b></TableCell>
                <TableCell align="center"><b>Estado</b></TableCell>
                <TableCell align="right"><b>Pts ganhos</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum desafio encontrado
                </TableCell></TableRow>
              ) : filtered.map(d => {
                const pct = d.valor_objetivo > 0 ? Math.min(d.valor_poupado / d.valor_objetivo, 1) : 0;
                return (
                  <TableRow key={d.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span style={{ fontSize: 20 }}>{d.emoji}</span>
                        <Typography variant="body2" fontWeight={600}>{d.titulo}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">{d.user_id.substring(0, 12)}...</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <LinearProgress variant="determinate" value={pct * 100}
                        sx={{ borderRadius: 1, height: 6,
                          '& .MuiLinearProgress-bar': { bgcolor: '#10B981' } }} />
                      <Typography variant="caption" color="text.secondary">{(pct * 100).toFixed(0)}%</Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(Number(d.valor_poupado))}</TableCell>
                    <TableCell align="right">{fmt(Number(d.valor_objetivo))}</TableCell>
                    <TableCell align="center">
                      <Chip label={d.estado} size="small" color={estadoColor[d.estado]} />
                    </TableCell>
                    <TableCell align="right">
                      {d.pontos_ganhos > 0 ? `⭐ ${d.pontos_ganhos}` : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SavingsAdminPage;
